// React Query hooks for the WB agents admin surface (Phase C2 Group 3).
//
// Pairs with /api/v1/wb/agents/* — list / detail / create / generate
// pairing-code / deactivate / soft-delete. `enc_id` here is the encrypted
// scalar id (snake_case throughout the WB module). DTOs mirror
// server/src/modules/wb/agents/types.ts exactly.
//
// `useWbAgents` polls every 30s so the table's heartbeat-derived status
// stays fresh without operator action. The plaintext `pairing_code` is
// only ever returned by the one-shot generation endpoint — the row
// listing exposes the expiry timestamp but never the code itself.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbAgentDto on the server) -------------------

export type WbAgentStatus = 'online' | 'offline' | 'unpaired';

export interface WbAgent {
  readonly enc_id: string;
  readonly hostname: string | null;
  readonly version: string | null;
  /** Pairing-lifecycle flag (online once paired, unpaired after deactivate). */
  readonly status: WbAgentStatus;
  /** Real presence: true when the server's last_heartbeat_at is within the
   *  90s freshness window. The status badge reads this, not `status`. */
  readonly is_online: boolean;
  /** enc_id of the weighbridge this agent is pinned to, or null. */
  readonly weighbridge_enc_id: string | null;
  /** Stable `code` of the pinned weighbridge, or null. enc_id rotates per
   *  response (random IV), so the assignment dropdown keys its selected state
   *  on this code — matched against the weighbridges list — not the enc_id. */
  readonly weighbridge_code: string | null;
  readonly last_heartbeat_at: string | null;
  readonly paired_at: string | null;
  readonly pairing_code_expires_at: string | null;
  readonly created_at: string;
}

export interface CreateAgentInput {
  name: string;
}

export interface PairingCodeResult {
  readonly pairing_code: string;
  readonly expires_at: string;
}

// -- Queries ----------------------------------------------------------------

export function useWbAgents() {
  return useQuery<{ agents: WbAgent[] }>({
    queryKey: queryKeys.wb.agents(),
    queryFn: async () => {
      const res = await apiClient.get<{ agents: WbAgent[] }>(endpoints.wb.agents);
      return res.data;
    },
    // The agent's WS connection stamps last_heartbeat_at on connect and every
    // 30s; the server derives `is_online` from its freshness (90s window). A
    // 30s poll keeps the table within one tick of reality.
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useWbAgent(encId: string | undefined) {
  const enabled = !!encId;
  return useQuery<{ agent: WbAgent }>({
    queryKey: queryKeys.wb.agent(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbAgent called without enc_id');
      const res = await apiClient.get<{ agent: WbAgent }>(endpoints.wb.agent(encId));
      return res.data;
    },
    enabled,
    staleTime: 15_000,
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation<{ agent: WbAgent }, Error, CreateAgentInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ agent: WbAgent }, CreateAgentInput>(
        endpoints.wb.agents,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agents() });
    },
  });
}

/**
 * POST /wb/agents/:enc_id/pairing-code — generates a fresh 15-minute
 * pairing code for an existing agent. The plaintext code is returned
 * once in the response and never re-fetched. Invalidates the list so
 * the row's `pairing_code_expires_at` reflects the new expiry.
 */
export function useGeneratePairingCode() {
  const qc = useQueryClient();
  return useMutation<PairingCodeResult, Error, { agentEncId: string }>({
    mutationFn: async ({ agentEncId }) => {
      const res = await apiClient.post<PairingCodeResult, Record<string, never>>(
        endpoints.wb.agentPairingCode(agentEncId),
        {},
      );
      return res.data;
    },
    onSuccess: (_data, { agentEncId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agents() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agent(agentEncId) });
    },
  });
}

/**
 * POST /wb/agents/:enc_id/deactivate — marks the agent unpaired and
 * wipes its pairing material. The agent device must re-pair to come
 * back online.
 */
export function useDeactivateAgent() {
  const qc = useQueryClient();
  return useMutation<{ agent: WbAgent }, Error, { agentEncId: string }>({
    mutationFn: async ({ agentEncId }) => {
      const res = await apiClient.post<{ agent: WbAgent }, Record<string, never>>(
        endpoints.wb.agentDeactivate(agentEncId),
        {},
      );
      return res.data;
    },
    onSuccess: (_data, { agentEncId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agents() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agent(agentEncId) });
    },
  });
}

/**
 * PATCH /wb/agents/:enc_id — pin (or clear) the agent's weighbridge
 * assignment. `weighbridgeEncId: null` clears it. The numeric id flows to
 * the agent via GET /agent/config and onto every weight_event it pushes.
 */
export function useAssignAgentWeighbridge() {
  const qc = useQueryClient();
  return useMutation<
    { agent: WbAgent },
    Error,
    { agentEncId: string; weighbridgeEncId: string | null }
  >({
    mutationFn: async ({ agentEncId, weighbridgeEncId }) => {
      const res = await apiClient.patch<
        { agent: WbAgent },
        { weighbridge_enc_id: string | null }
      >(endpoints.wb.agent(agentEncId), { weighbridge_enc_id: weighbridgeEncId });
      return res.data;
    },
    onSuccess: (_data, { agentEncId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agents() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agent(agentEncId) });
    },
  });
}

/**
 * DELETE /wb/agents/:enc_id — soft-deletes the agent record. The row
 * disappears from the list on success.
 */
export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation<void, Error, { agentEncId: string }>({
    mutationFn: async ({ agentEncId }) => {
      await apiClient.delete<{ deleted: true }>(endpoints.wb.agent(agentEncId));
    },
    onSuccess: (_data, { agentEncId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agents() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.agent(agentEncId) });
    },
  });
}
